import { ESTADOS_LABELS, ORIGENES_LABELS } from "../constants";
import DateUtils from "../../../utils/DateUtils";

/**
 * Helpers para lógica de negocio de incidencias
 * Transformaciones complejas y generación de datos para el dominio
 */

/**
 * Obtiene un modelo relacionado de la factura de Sage por su prefijo.
 * Las asociaciones no tienen alias, así que la clave lleva el año cuando la
 * incidencia no es del ejercicio actual (ClientesModel2025, EnviCliModel2025).
 * @param {Object} invoice - Factura de Sage adjunta a la incidencia
 * @param {string} prefijo - Nombre del modelo sin año (ej: "ClientesModel")
 * @returns {Object|null} Datos del modelo relacionado, o null si no viene
 */
export const obtenerRelacionFactura = (invoice, prefijo) => {
  if (!invoice) return null;
  // Solo el prefijo seguido de dígitos (o nada), para no confundir modelos de nombre parecido
  const clave = Object.keys(invoice).find(
    (k) => k.startsWith(prefijo) && /^\d*$/.test(k.slice(prefijo.length))
  );
  return clave ? invoice[clave] ?? null : null;
};

/**
 * Transforma los datos de incidencias del API al formato del grid
 * @param {Array} incidencias - Lista de incidencias del API (camelCase)
 * @returns {Array} Incidencias transformadas para el grid
 */
export const transformarIncidenciasParaGrid = (incidencias) => {
  return incidencias.map((incidencia) => {
    // Transformar comentarios (camelCase → snake_case para modal legacy)
    const comentariosTransformados = (incidencia.comentarios || []).map(
      (comentario) => ({
        ...comentario,
        nombre_empleado: comentario.nombreEmpleado,
        email_enviado_a: comentario.emailEnviadoA,
      })
    );

    // Calcular referencia según tipo
    let referencia = "";
    if (incidencia.tipoNote) {
      const tipo = incidencia.tipoNote.trim();
      if (tipo === "art") {
        referencia = incidencia.codigoArticulo?.trim() || "";
      } else if (tipo === "ped") {
        referencia = incidencia.albaran?.trim() || "";
      } else {
        referencia =
          (incidencia.serie?.trim() || "") +
          "-" +
          (incidencia.albaran?.trim() || "");
      }
    }

    // Datos del cliente si existe factura
    let cliente = "";
    let factura = null;
    let direccion = "";
    let tituloCliente = incidencia.note;

    if (incidencia.invoice) {
      factura = incidencia.invoice.FACTURA ?? null;
      const clienteFactura = obtenerRelacionFactura(incidencia.invoice, "ClientesModel");
      if (clienteFactura) {
        tituloCliente = clienteFactura.NOMBRE;
        cliente = clienteFactura.CODIGO + " \\ " + clienteFactura.NOMBRE;
      }

      // Dirección de envío de la factura (EnviCliModel de Sage)
      const envio = obtenerRelacionFactura(incidencia.invoice, "EnviCliModel");
      if (envio) {
        const cpPoblacion = [envio.CODPOS, envio.POBLACION]
          .filter(Boolean)
          .join(" ");
        direccion = [envio.DIRECCION, cpPoblacion, envio.PROVINCIA]
          .map((parte) => (parte || "").trim())
          .filter(Boolean)
          .join(", ");
      }
    }

    return {
      // Campos del grid
      id: incidencia.idNote,
      idNote: incidencia.idNote,
      titulo: tituloCliente,
      referencia,
      tipoNote: incidencia.tipoNote,
      // Origen: código original (para color del badge) + label (para mostrar/ordenar)
      origen: incidencia.origen || "",
      origenLabel: ORIGENES_LABELS[incidencia.origen] || incidencia.origen || "",
      nombreEmpleado: incidencia.nombreEmpleado,
      estado: incidencia.estado,
      estadoTexto: ESTADOS_LABELS[incidencia.estado] || incidencia.estado || "",
      fechaCreado: DateUtils.formatearFechaHora(incidencia.creado),
      fechaModificado: DateUtils.formatearFechaHora(incidencia.modificado),

      // Campos para el modal (snake_case legacy)
      id_note: incidencia.idNote,
      tipo_note: incidencia.tipoNote,
      nombre_empleado: incidencia.nombreEmpleado,
      codigo_articulo: incidencia.codigoArticulo,
      id_empleado: incidencia.idEmpleado,
      serie: incidencia.serie,
      albaran: incidencia.albaran,
      note: incidencia.note,
      comentarios: comentariosTransformados,
      invoice: incidencia.invoice,
      cliente,
      factura,
      direccion,
    };
  });
};

/**
 * Genera el texto HTML para mostrar en el modal según el tipo de incidencia
 * @param {Object} incidencia - Incidencia seleccionada
 * @returns {string} HTML formateado
 */
export const generarTextoModalIncidencia = (incidencia) => {
  if (!incidencia.tipo_note) return "";

  const tipo = incidencia.tipo_note.trim();
  let text = "";

  switch (tipo) {
    case "art":
      text = "<strong>" + (incidencia.codigo_articulo?.trim() || "") + "</strong>";
      break;
    case "ped":
      text = "<strong>" + (incidencia.albaran?.trim() || "") + "</strong><br>";
      break;
    default:
      text =
        "<strong>" +
        (incidencia.serie?.trim() || "") +
        "-" +
        (incidencia.albaran?.trim() || "") +
        "</strong><br>" +
        (incidencia.cliente || "") +
        (incidencia.factura
          ? (incidencia.cliente ? " - " : "") +
            "<strong>Factura:</strong> " +
            incidencia.factura
          : "") +
        (incidencia.direccion
          ? "<br><strong>Dirección:</strong> " + incidencia.direccion
          : "");
  }

  return "Datos de la incidencia: " + text;
};
